import { Spine } from "@esotericsoftware/spine-pixi-v8";
import * as PIXI from "pixi.js";
import { useCallback, useRef, useState, useEffect } from "preact/hooks";
import { GameState, GameStateEnum } from "../../../shared/types/global.types";
import { useSkinStore, resolveSpineAsset } from "../../../store/skinStore";
import { useSpineHelpers } from "./useSpineHelpers";

export enum StairsAnim {
  IDLE = "02_st_idle",
  CLIMB = "04_st_climb",
  FALL = "05_st_fall",
}

const STAIRS_ANIMS = [
  StairsAnim.IDLE,
  StairsAnim.CLIMB,
  StairsAnim.FALL,
] as const;

interface UseSpineStairsProps {
  gameState?: GameState;
  onAnimationComplete?: (animationName: string) => void;
}

export function useSpineStairs({
  gameState,
  onAnimationComplete,
}: UseSpineStairsProps) {
  const { getSkin } = useSkinStore();
  const [currentAnim, setCurrentAnim] = useState<StairsAnim | null>(null);
  const stairsSpineRef = useRef<Spine | null>(null);
  const anims = STAIRS_ANIMS;
  const { fitAndPosition } = useSpineHelpers();

  const createStairs = useCallback(
    async (parent: PIXI.Container, w: number, h: number) => {
      try {
        const skin = getSkin("pioneer");
        if (!skin.stairs) {
          throw new Error("Stairs asset not found in skin");
        }
        const resolved = resolveSpineAsset(skin.stairs);
        const skel = resolved.skeleton;
        const atl = resolved.atlas;
        await PIXI.Assets.load([skel, atl]);
        const stairsSpine = Spine.from({
          skeleton: skel,
          atlas: atl,
          scale: 1,
          autoUpdate: true,
        });

        stairsSpine.roundPixels = false;
        stairsSpine.zIndex = 1; // Выше sky фона
        stairsSpineRef.current = stairsSpine;

        parent.addChild(stairsSpine);
        try {
          parent.setChildIndex(stairsSpine, 1);
        } catch {
          // ignore
        }

        fitAndPosition(stairsSpine, w, h);

        try {
          const idle = StairsAnim.IDLE;
          stairsSpine.state.setAnimation(0, idle, true);
          setCurrentAnim(idle);
        } catch (e) {
          console.warn("useSpineStairs: не удалось запустить idle анимацию", e);
        }

        return stairsSpine;
      } catch (e) {
        console.error("useSpineStairs: ошибка создания лестницы:", e);
        throw e;
      }
    },
    [fitAndPosition, getSkin]
  );

  const resizeStairs = useCallback(
    (w: number, h: number) => {
      const spine = stairsSpineRef.current;
      if (!spine) return;
      fitAndPosition(spine, w, h);
    },
    [fitAndPosition]
  );

  useEffect(() => {
    const spine = stairsSpineRef.current;
    if (!spine) return;

    const play = (name: StairsAnim, loop: boolean) => {
      try {
        const entry = spine.state.setAnimation(0, name, loop);
        setCurrentAnim(name);
        return entry;
      } catch (e) {
        console.warn("useSpineStairs: cannot play", name, e);
        return null;
      }
    };

    if (gameState === GameStateEnum.COUNTDOWN) {
      const idleAnim = StairsAnim.IDLE;
      if (currentAnim !== idleAnim) {
        play(idleAnim, true);
      }
      return;
    }

    if (gameState === GameStateEnum.CLIMBING) {
      const climbAnim = StairsAnim.CLIMB;
      if (currentAnim !== climbAnim) {
        play(climbAnim, true);
      }
      return;
    }

    if (gameState === GameStateEnum.CRASHED) {
      const fallAnim = StairsAnim.FALL;
      if (currentAnim !== fallAnim) {
        play(fallAnim, false);
      }
      return;
    }
  }, [gameState, currentAnim, onAnimationComplete]);

  const setClimbActive = useCallback((active: boolean) => {
    const spine = stairsSpineRef.current;
    if (!spine) return;
    try {
      if (active) {
        const climbAnim = StairsAnim.CLIMB;
        spine.state.setAnimation(0, climbAnim, true);
        setCurrentAnim(climbAnim);
      } else {
        const idleAnim = StairsAnim.IDLE;
        spine.state.setAnimation(0, idleAnim, true);
        setCurrentAnim(idleAnim);
      }
    } catch (e) {
      console.warn(
        "useSpineStairs: не удалось переключить анимацию лестницы",
        e
      );
    }
  }, []);

  const changeAnimation = useCallback((name: string) => {
    const spine = stairsSpineRef.current;
    if (!spine) return;
    if (!STAIRS_ANIMS.includes(name as StairsAnim)) return;
    const typedName = name as StairsAnim;
    try {
      const looping =
        typedName === StairsAnim.IDLE || typedName === StairsAnim.CLIMB;
      spine.state.setAnimation(0, typedName, looping);
      setCurrentAnim(typedName);
    } catch (e) {
      console.error("useSpineStairs: ошибка при смене анимации", e);
    }
  }, []);

  const destroyStairs = useCallback(() => {
    const spine = stairsSpineRef.current;
    if (!spine) return;
    spine.destroy({ children: true });
    stairsSpineRef.current = null;
    setCurrentAnim(null);
  }, []);

  return {
    stairsSpineRef,
    anims,
    currentAnim,
    createStairs,
    resizeStairs,
    setClimbActive,
    changeAnimation,
    destroyStairs,
    spine: stairsSpineRef.current,
  } as const;
}
